import React, { useState, useRef, useEffect } from "react";
import PersonalDetails from "./PersonalDetails";
import ContactInformation from "./ContactInformation";
import Training from "./Training";
import AdditionalDetails from "./AdditionalDetails";
import FormWrapper from "./FormWrapper";
import AvailabilityPeriods from "./AvailabilityPeriods";
import FinanceInfo from "./FinanceInfo";
import PropTypes from "prop-types";
import SubMenu from "../../../components/templates/SubMenu";
import Spacer from "../../../components/layouts/Spacer";
import ContentSider from "../../../components/templates/ContentSider";
import WorkPeriods from "./WorkPeriods";
import { FormattedMessage } from "react-intl";
import Relatives from "./Relatives";
import { useSelector } from "react-redux";
import moment from "moment";
import PrintFormsTable from "../../../components/companyforms/PrintFormsTable";
import VisibilitySensor from "react-visibility-sensor";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { Layout, Typography } from "antd";
import {
  AuditOutlined,
  CarryOutOutlined,
  ExceptionOutlined,
} from "@ant-design/icons";
const { Text } = Typography;
const { Content } = Layout;

const getMenuItems = () => [
  {
    name: "personalDetails",
    category: "employees",
    icon: <AuditOutlined />,
  },
  {
    name: "contactInformation",
    category: "employees",
    icon: <CarryOutOutlined />,
  },
  {
    name: "training",
    category: "employees",
    icon: <ExceptionOutlined />,
  },
  {
    name: "workPeriods",
    category: "employees",
    icon: <CarryOutOutlined />,
  },
  {
    name: "financeInfo",
    category: "employees",
    icon: <CarryOutOutlined />,
  },
  {
    name: "relatives",
    category: "employees",
    icon: <AuditOutlined />,
  },
  {
    name: "availability",
    category: "employees",
    icon: <ExceptionOutlined />,
  },
  ...(isAllowedFeature("forms")
    ? [
        {
          name: "forms",
          category: "employees",
          icon: <CarryOutOutlined />,
        },
      ]
    : []),
];

const Informatiom = (props) => {
  const {
    mode,
    onSubmit,
    onFinishFailed,
    onDecline,
    saveLoading,
    activeEmployee,
    loadEmployee,
  } = props;

  const values = useSelector(
    (state) => state.employeeDetails.changedItems.values
  );
  const [selectedKeys, setSelectedKeys] = useState("personalDetails");
  const [visibleKeys, setVisibleKeys] = useState([
    { itemKey: "personalDetails", weight: 1 },
  ]);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const [waitingForExport, setWaitingForExport] = useState(false);
  const personalDetails = useRef();
  const contactInformation = useRef();
  const training = useRef();
  const availability = useRef();
  const relatives = useRef();
  const workPeriods = useRef();
  const financeInfo = useRef();
  const forms = useRef();

  let refs = {
    personalDetails: personalDetails,
    contactInformation: contactInformation,
    workPeriods: workPeriods,
    financeInfo: financeInfo,
    relatives: relatives,
    training: training,
    availability: availability,
  };
  if (isAllowedFeature("forms")) refs["forms"] = forms;

  useEffect(() => {
    visibleKeys.sort((a, b) => a.weight - b.weight);

    if (scrollEnabled) {
      setSelectedKeys(visibleKeys[0] ? [visibleKeys[0].itemKey] : []);
    }
  }, [visibleKeys, scrollEnabled]);

  useEffect(() => {
    setWaitingForExport(activeEmployee.exported_at === null ? true : false);
  }, [activeEmployee]);

  const onMenuSelect = ({ key }) => {
    if ([key] && refs[key].current) {
      setScrollEnabled(false);
      refs[key].current.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => setScrollEnabled(true), 1000);
    }
    setSelectedKeys([key]);
  };

  const onVisiblityChanged = (itemKey, weight) => (isVisible) => {
    if (isVisible) {
      setVisibleKeys([...visibleKeys, { itemKey, weight }]);
    } else {
      setVisibleKeys([
        ...visibleKeys.filter((item) => item.itemKey !== itemKey),
      ]);
    }
  };

  const onExportChange = (checked) => {
    setWaitingForExport(checked);
  };

  return (
    <Layout
      style={{
        background: "transparent",
        height: "100%",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Spacer horizontal={true} />
      <ContentSider width={250}>
        <SubMenu
          onMenuSelect={onMenuSelect}
          items={getMenuItems()}
          selectedKeys={selectedKeys}
        />
      </ContentSider>
      <Content>
        <FormWrapper
          mode={mode}
          onSubmit={onSubmit}
          onFinishFailed={onFinishFailed}
          hideDelete={true}
          onDecline={onDecline}
          saveLoading={saveLoading}
          padding={true}
          waitingForExport={waitingForExport}
        >
          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("personalDetails", 1)}
          >
            <PersonalDetails
              ref={personalDetails}
              activeEmployee={activeEmployee}
              values={values}
            />
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("contactInformation", 2)}
          >
            <div>
              <ContactInformation ref={contactInformation} />
              <AdditionalDetails activeEmployee={activeEmployee} />
            </div>
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("training", 3)}
          >
            <Training ref={training} />
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("workPeriods", 4)}
          >
            <WorkPeriods
              activeEmployee={activeEmployee}
              loadEmployee={loadEmployee}
              ref={workPeriods}
            />
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("financeInfo", 5)}
          >
            <FinanceInfo
              ref={financeInfo}
              values={values}
              activeEmployee={activeEmployee}
              waitingForExport={waitingForExport}
              onExportChange={onExportChange}
            />
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("relatives", 6)}
          >
            <Relatives ref={relatives} />
          </VisibilitySensor>

          <VisibilitySensor
            scrollThrottle={200}
            offset={{ top: 220 }}
            scrollCheck
            partialVisibility={true}
            onChange={onVisiblityChanged("availability", 8)}
          >
            <AvailabilityPeriods
              ref={availability}
              activeEmployee={activeEmployee}
            />
          </VisibilitySensor>
          {isAllowedFeature("forms") && (
            <VisibilitySensor
              scrollThrottle={200}
              offset={{ top: 220 }}
              scrollCheck
              partialVisibility={true}
              onChange={onVisiblityChanged("forms", 8)}
            >
              <PrintFormsTable
                ref={forms}
                entity="employee"
                entityId={activeEmployee.id}
                entityCollection="employees"
              />
            </VisibilitySensor>
          )}
        </FormWrapper>

        {activeEmployee && activeEmployee.UpdatedAt ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                padding: "20px",
                display: "flex",
                alignItems: "center ",
              }}
            >
              <Text type="secondary">
                {<FormattedMessage id="app.employees.last_update" />}
              </Text>
              <Text type="secondary">{":"}</Text>
              <Text style={{ margin: "0 5px" }}>
                {activeEmployee && activeEmployee.UpdatedAt
                  ? moment(activeEmployee.UpdatedAt).calendar()
                  : ""}
              </Text>
            </div>
          </div>
        ) : null}
      </Content>
    </Layout>
  );
};

Informatiom.propTypes = {
  mode: PropTypes.string,
  onFinish: PropTypes.func,
  onFinishFailed: PropTypes.func,
  onDecline: PropTypes.func,
  addImage: PropTypes.func,
  saveLoading: PropTypes.bool,
  activeEmployee: PropTypes.object,
};

export default Informatiom;
